<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import KaptionSearchInput from 'dashboard/components-next/Campaigns/KaptionSearchInput.vue';
import KaptionFilterSelect from 'dashboard/components-next/Campaigns/KaptionFilterSelect.vue';
import KaptionWhatsAppPreviewEnhanced from 'dashboard/components-next/Campaigns/KaptionWhatsAppPreviewEnhanced.vue';
import {
  filterTemplates,
  getAvailableCategories,
  getAvailableLanguages,
} from 'dashboard/components-next/NewConversationModal/helpers/newConversationHelpers';

const props = defineProps({
  templates: { type: Array, default: () => [] },
  selectedTemplate: { type: Object, default: null },
});

const emit = defineEmits(['select', 'back']);

const { t } = useI18n();

const query = ref('');
const activeCategory = ref('ALL');
const activeLanguage = ref('ALL');
const fullPreviewTemplate = ref(null);
const visiblePreviewKeys = ref(new Set());
const previewObservers = new WeakMap();

const previewKey = template => `${template.name}-${template.language}`;
const showPreview = key => {
  if (visiblePreviewKeys.value.has(key)) return;
  visiblePreviewKeys.value = new Set([...visiblePreviewKeys.value, key]);
};

// Mount remote preview iframes only as their cards approach the scroll viewport.
// A large account can expose hundreds of templates; eager mounting made every
// iframe compete for the main thread and occasionally lose initialization.
const vLazyPreview = {
  mounted(element, { value }) {
    if (typeof IntersectionObserver === 'undefined') {
      showPreview(value);
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        showPreview(value);
        observer.disconnect();
        previewObservers.delete(element);
      },
      {
        root: element.parentElement,
        rootMargin: '120px 0px',
        threshold: 0.01,
      }
    );
    previewObservers.set(element, observer);
    observer.observe(element);
  },
  unmounted(element) {
    previewObservers.get(element)?.disconnect();
    previewObservers.delete(element);
  },
};

const categories = computed(() => getAvailableCategories(props.templates));
const languages = computed(() => getAvailableLanguages(props.templates));
const languageOptions = computed(() => [
  {
    value: 'ALL',
    label: t('NEW_CONVERSATION_MODAL.TEMPLATE.ALL_LANGUAGES'),
  },
  ...languages.value.map(language => ({ value: language, label: language })),
]);

const filtered = computed(() =>
  filterTemplates(props.templates, {
    query: query.value,
    category: activeCategory.value,
    language: activeLanguage.value,
  })
);
const fullPreviewMeta = computed(() => {
  if (!fullPreviewTemplate.value) return '';
  return [
    fullPreviewTemplate.value.language,
    fullPreviewTemplate.value.category || 'UTILITY',
  ]
    .filter(Boolean)
    .join(' · ');
});

const categoryBadge = template =>
  (template?.category || 'UTILITY').slice(0, 3).toUpperCase();

const selectTemplate = template => emit('select', template);
const openFullPreview = template => {
  fullPreviewTemplate.value = template;
};
const closeFullPreview = () => {
  fullPreviewTemplate.value = null;
};
</script>

<template>
  <div class="flex flex-col gap-4 min-h-[24rem]">
    <!-- Search + language. Shared Kaption controls keep icon/placeholder
         alignment stable inside Warm Paper and the classic layout. -->
    <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
      <KaptionSearchInput
        v-model="query"
        data-testid="template-search"
        size="md"
        width-class="w-full"
        :debounce="0"
        :placeholder="t('NEW_CONVERSATION_MODAL.TEMPLATE.SEARCH')"
      />
      <KaptionFilterSelect
        v-if="languages.length > 1"
        v-model="activeLanguage"
        :options="languageOptions"
        :placeholder="t('NEW_CONVERSATION_MODAL.TEMPLATE.ALL_LANGUAGES')"
        icon="i-lucide-languages"
      />
    </div>

    <!-- Category segmented filter -->
    <div
      class="inline-flex self-start flex-wrap gap-1 p-1 border rounded-lg border-n-weak bg-n-alpha-black2"
    >
      <button
        type="button"
        class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
        :class="
          activeCategory === 'ALL'
            ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
            : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-black4'
        "
        @click="activeCategory = 'ALL'"
      >
        {{ t('NEW_CONVERSATION_MODAL.TEMPLATE.ALL') }}
      </button>
      <button
        v-for="cat in categories"
        :key="cat"
        type="button"
        class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
        :class="
          activeCategory === cat
            ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
            : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-black4'
        "
        @click="activeCategory = cat"
      >
        {{ t(`NEW_CONVERSATION_MODAL.TEMPLATE.CATEGORY.${cat}`) }}
      </button>
    </div>

    <!-- Gallery grid. The iframe is the card: users choose the same rendered
         WhatsApp preview they will customize in the next step. -->
    <div
      v-if="filtered.length"
      class="grid flex-1 grid-cols-1 gap-3 overflow-y-auto sm:grid-cols-2 max-h-[31rem] pr-1"
    >
      <article
        v-for="template in filtered"
        :key="`${template.name}-${template.language}`"
        v-lazy-preview="previewKey(template)"
        data-testid="template-card"
        class="relative flex flex-col overflow-hidden transition-all border rounded-xl bg-n-solid-1 hover:border-n-strong hover:shadow-md"
        :class="
          selectedTemplate?.name === template.name
            ? 'border-n-brand ring-1 ring-n-brand'
            : 'border-n-weak'
        "
      >
        <button
          type="button"
          class="absolute inset-0 z-20 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-n-brand"
          :aria-label="template.name"
          @click="selectTemplate(template)"
        />

        <div
          class="flex items-center justify-between gap-2 px-3 py-2.5 border-b border-n-weak"
        >
          <span class="min-w-0">
            <span class="block text-sm font-semibold truncate text-n-slate-12">
              {{ template.name }}
            </span>
            <span
              class="block text-[11px] uppercase tracking-wide text-n-slate-9"
            >
              {{ template.language }}
            </span>
          </span>
          <span class="flex items-center gap-1 shrink-0">
            <span
              class="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-n-alpha-black2 text-n-slate-11"
            >
              {{ categoryBadge(template) }}
            </span>
            <button
              type="button"
              data-testid="full-preview-button"
              class="relative z-30 flex items-center justify-center transition-colors border rounded-md size-7 border-n-weak bg-n-solid-1 text-n-slate-10 hover:border-n-strong hover:bg-n-alpha-black2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
              :aria-label="
                t('NEW_CONVERSATION_MODAL.TEMPLATE.VIEW_FULL', {
                  name: template.name,
                })
              "
              :title="t('NEW_CONVERSATION_MODAL.TEMPLATE.VIEW_FULL_SHORT')"
              @click.stop="openFullPreview(template)"
            >
              <!-- Kaption Style Guide: Eye (Privacy), inline SVG. -->
              <svg
                aria-hidden="true"
                class="size-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"
                />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>
          </span>
        </div>

        <div
          class="h-[15rem] overflow-hidden pointer-events-none bg-n-alpha-black1"
          aria-hidden="true"
        >
          <KaptionWhatsAppPreviewEnhanced
            v-if="visiblePreviewKeys.has(previewKey(template))"
            :template="template"
            bare
            truncate
            mode="render"
            class="h-full !rounded-none"
          />
          <div
            v-else
            class="flex items-center justify-center h-full bg-n-alpha-black1"
            data-testid="preview-placeholder"
          >
            <span
              class="border-2 rounded-full size-5 animate-spin border-n-weak border-t-n-brand"
            />
          </div>
        </div>
      </article>
    </div>

    <!-- Empty state -->
    <div
      v-else
      class="flex flex-col items-center justify-center flex-1 gap-2 py-10 text-center"
      data-testid="template-empty"
    >
      <span class="i-lucide-layout-template size-8 text-n-slate-9" />
      <p class="text-sm text-n-slate-11">
        {{ t('NEW_CONVERSATION_MODAL.TEMPLATE.EMPTY') }}
      </p>
    </div>

    <!-- Footer -->
    <div class="flex justify-between pt-2">
      <Button
        :label="t('NEW_CONVERSATION_MODAL.BACK')"
        icon="i-lucide-arrow-left"
        variant="ghost"
        color="slate"
        size="sm"
        @click="emit('back')"
      />
    </div>

    <teleport to="body">
      <div
        v-if="fullPreviewTemplate"
        class="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-n-alpha-black2 backdrop-blur-[6px]"
        data-testid="full-preview-dialog"
        role="dialog"
        aria-modal="true"
        :aria-label="
          t('NEW_CONVERSATION_MODAL.TEMPLATE.FULL_PREVIEW', {
            name: fullPreviewTemplate.name,
          })
        "
        @click.self="closeFullPreview"
      >
        <section
          class="flex w-full max-w-2xl max-h-[calc(100vh-2rem)] flex-col overflow-hidden border shadow-2xl rounded-xl border-n-strong bg-n-alpha-3 backdrop-blur-[100px]"
        >
          <header
            class="flex items-center justify-between gap-3 px-4 py-3 border-b border-n-weak"
          >
            <div class="min-w-0">
              <h3 class="text-sm font-semibold truncate text-n-slate-12">
                {{ fullPreviewTemplate.name }}
              </h3>
              <p class="text-[11px] uppercase tracking-wide text-n-slate-9">
                {{ fullPreviewMeta }}
              </p>
            </div>
            <Button
              :label="t('NEW_CONVERSATION_MODAL.TEMPLATE.CLOSE_PREVIEW')"
              variant="ghost"
              color="slate"
              size="sm"
              data-testid="full-preview-close"
              @click="closeFullPreview"
            />
          </header>

          <div class="min-h-0 flex-1 p-4 bg-n-alpha-black1">
            <KaptionWhatsAppPreviewEnhanced
              data-testid="full-preview-renderer"
              :template="fullPreviewTemplate"
              bare
              :truncate="false"
              mode="render"
              class="h-[min(72vh,44rem)] min-h-[28rem] border rounded-xl border-n-weak"
            />
          </div>
        </section>
      </div>
    </teleport>
  </div>
</template>
